import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCart from '../../hooks/useCart';
import { useAuth } from '../../hooks/useAuth';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import useWindowSize from '../../hooks/useWindowSize';

const Header = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartItemCount } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  
  // Memoize frequently changing values to prevent unnecessary re-renders
  const stableUser = useMemo(() => user, [user?.id, user?.email]);
  const stableAuth = useMemo(() => ({ isAuthenticated, logout }), [isAuthenticated, logout]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const userMenuRef = useRef(null);
  const userMenuTimeoutRef = useRef(null);
  
  // ROBUST RESPONSIVE DEVICE DETECTION
  const { width: windowWidth } = useWindowSize();
  const [navElementRef, setNavElementRef] = useState(null);
  const [actualNavWidth, setActualNavWidth] = useState(0);
  
  // STABLE: Create stable values to prevent infinite loops
  const stableWindowWidth = useMemo(() => Math.round(windowWidth / 10) * 10, [windowWidth]);
  const stableNavWidth = useMemo(() => actualNavWidth ? Math.round(actualNavWidth / 20) * 20 : 800, [actualNavWidth]);

  // STABLE: Memoize deviceConfig with rounded values to prevent micro-changes
  const deviceConfig = useMemo(() => {
    const isMobile = stableWindowWidth <= 768;
    const isTablet = stableWindowWidth <= 1024;
    const isSmallDesktop = stableWindowWidth <= 1200;
    const isMediumDesktop = stableWindowWidth <= 1440;
    const isLargeDesktop = stableWindowWidth <= 1920;
    const isUltraWide = stableWindowWidth > 1920;
    
    const isLowEnd = (
      isMobile ||
      ('connection' in navigator && (
        navigator.connection?.effectiveType === 'slow-2g' ||
        navigator.connection?.effectiveType === '2g' ||
        navigator.connection?.saveData === true
      )) ||
      ('deviceMemory' in navigator && navigator.deviceMemory < 4) ||
      ('hardwareConcurrency' in navigator && navigator.hardwareConcurrency < 4)
    );
    
    // STABLE: Calculate available space with rounded values
    const logoWidth = isMobile ? 120 : 180;
    const rightActionsWidth = isMobile ? 80 : stableWindowWidth >= 1440 ? 240 : 180;
    const padding = isMobile ? 32 : 48;
    const minNavWidth = 400; // Minimum space required for navigation
    const availableNavWidth = Math.max(minNavWidth, stableWindowWidth - logoWidth - rightActionsWidth - padding);
    
    // STABLE: Progressive responsiveness with bounded values
    const requiredNavWidth = stableNavWidth || 800; // Fallback estimate  
    const spaceRatio = requiredNavWidth > 0 ? Math.max(0.5, Math.min(2.0, availableNavWidth / requiredNavWidth)) : 1.0;
    
    return {
      isMobile,
      isTablet,
      isSmallDesktop,
      isMediumDesktop,
      isLargeDesktop,
      isUltraWide,
      isLowEnd,
      windowWidth: stableWindowWidth,
      availableNavWidth,
      spaceRatio,
      requiredNavWidth,
      
      // STABLE: Features with hysteresis to prevent flickering
      showCartText: stableWindowWidth >= 1440,
      showFullNavigation: stableWindowWidth >= 768,
      useCompactMode: spaceRatio < 0.95 && stableWindowWidth > 768, // Slightly more aggressive threshold
      useMicroMode: spaceRatio < 0.75 && stableWindowWidth > 768,   // Lower threshold for emergency mode
      hideSecondaryItems: spaceRatio < 0.55 && stableWindowWidth > 768, // Only hide when really needed
      
      enableGlassEffects: !isLowEnd && !isMobile && !isTablet,
      enableAnimations: !isLowEnd && !isMobile,
      enableBlur: false
    };
  }, [stableWindowWidth, stableNavWidth]);

  // STABLE: Debounced navigation width measurement to prevent infinite loops
  useEffect(() => {
    if (navElementRef && windowWidth > 768) {
      // Skip ResizeObserver for high zoom levels to prevent feedback loops
      const devicePixelRatio = window.devicePixelRatio;
      if (devicePixelRatio > 1.2) {
        // For high zoom, use static calculations based on window width
        const estimatedNavWidth = Math.min(800, Math.max(400, windowWidth * 0.4));
        setActualNavWidth(estimatedNavWidth);
        return;
      }

      let measurementTimeout;
      
      const resizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) {
          const newWidth = entry.contentRect.width;
          
          // Debounce measurements to prevent rapid changes
          clearTimeout(measurementTimeout);
          measurementTimeout = setTimeout(() => {
            // Only update if there's a significant change (>10px difference)
            setActualNavWidth(prevWidth => {
              const difference = Math.abs(newWidth - (prevWidth || 0));
              if (difference > 10 || prevWidth === 0) {
                return newWidth;
              }
              return prevWidth;
            });
          }, 100); // 100ms debounce
        }
      });
      
      resizeObserver.observe(navElementRef);
      return () => {
        resizeObserver.disconnect();
        clearTimeout(measurementTimeout);
      };
    }
  }, [navElementRef, windowWidth]);

  // PERFORMANCE: Use Intersection Observer instead of scroll events
  const headerRef = useRef(null);
  const scrollSentinelRef = useRef(null);
  const rafRef = useRef(null);
  const isIntersectingRef = useRef(true);
  const stableScrollStateRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const isHighZoomRef = useRef(window.devicePixelRatio > 1.2);
  const [isHighZoom, setIsHighZoom] = useState(window.devicePixelRatio > 1.2);

  const cartItemCount = useMemo(() => getCartItemCount(), [getCartItemCount]);

  // Parent categories for Parfüm Malzemeleri dropdown (excluding Esanslar)
  const parfumMalzemeleriCategories = useMemo(() => [
    { id: '0edcb00e-f5a5-4367-91cc-d90d82577e5e', name: 'Parfüm Şişeleri' },
    { id: '4e803d62-dd28-42bc-a623-23cc8f0b771f', name: 'Parfüm Kapakları' },
    { id: 'b6dd881a-8158-4c5c-8572-e0d123d0c5f7', name: 'Pompa & Yüzük' },
    { id: 'a5c825d2-42db-4d26-92bb-d233721d18e3', name: 'Mini Set' },
    { id: 'eedb8757-78c2-49e5-8c6b-d64133bbe794', name: 'Difüzör' },
    { id: '11fa51c2-90cd-48c3-8a69-f2bcced1aa45', name: 'Serum Şişeleri' },
    {id: "428e41c1-cb4b-464c-9b6b-eeb7724ae10a" , name: 'Parfüm Yapım Kiti'},
    { id: '542e1ae2-e3e9-47b0-ac88-ffa912999a9c', name: 'Oto Kokusu' }
  ], []);

  // Memoize navigation items
  const navItems = useMemo(() => [
    { path: '/', key: 'nav.home' },
    //{ path: '/campaigns', key: 'nav.campaigns', fallback: 'Campaigns' },
    { path: '/blog', key: 'nav.blog', fallback: 'Blog' },
    { path: '/configurator', key: 'nav.configurator', fallback: 'Design Tool' },
    { path: '/faq', key: 'nav.faq' },
    { path: '/contact', key: 'nav.contact' },
    { path: '/orders/search', key: 'nav.trackOrder', fallback: 'Track Order' }
  ], []);

  // PERFORMANCE: Robust intersection observer callback with debouncing
  const handleIntersection = useCallback((entries) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Clear any existing debounce
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    const updateScrollState = () => {
      const entry = entries[0];
      const intersectionRatio = entry.intersectionRatio;
      
      // Use intersection ratio for more stable detection
      // Add hysteresis: require more significant change to prevent jitter
      const scrollThreshold = 0.1; // 10% intersection
      const shouldBeScrolled = intersectionRatio < scrollThreshold;
      
      // Only update if state actually changed and it's been stable
      if (stableScrollStateRef.current !== shouldBeScrolled) {
        stableScrollStateRef.current = shouldBeScrolled;
        
        // Debounce state changes to prevent rapid toggling
        debounceTimeoutRef.current = setTimeout(() => {
          setIsScrolled(shouldBeScrolled);
          isIntersectingRef.current = !shouldBeScrolled;
        }, 16); // One frame delay for stability
      }
    };
    
    rafRef.current = requestAnimationFrame(updateScrollState);
  }, []);

  // PERFORMANCE: Use Intersection Observer for header state
  useEffect(() => {
    if (!deviceConfig.enableAnimations) {
      return;
    }

    const shippingBarPx = (() => {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue('--shipping-announcement-height')
        .trim();
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : 36;
    })();
    const scrollRevealInsetPx = 80 + shippingBarPx;

    // Detect high zoom levels that cause issues
    const devicePixelRatio = window.devicePixelRatio;
    const highZoom = devicePixelRatio > 1.2;
    isHighZoomRef.current = highZoom;
    setIsHighZoom(highZoom);

    let observer = null;
    let scrollHandler = null;

    if (highZoom) {
      // Use scroll event for high zoom levels to avoid intersection observer issues
      let lastScrollY = 0;
      let ticking = false;

      scrollHandler = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            const shouldBeScrolled = currentScrollY > scrollRevealInsetPx;
            
            if (Math.abs(currentScrollY - lastScrollY) > 5) { // Threshold to prevent micro-changes
              if (shouldBeScrolled !== stableScrollStateRef.current) {
                stableScrollStateRef.current = shouldBeScrolled;
                setIsScrolled(shouldBeScrolled);
                isIntersectingRef.current = !shouldBeScrolled;
              }
              lastScrollY = currentScrollY;
            }
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', scrollHandler, { passive: true });
    } else if (scrollSentinelRef.current) {
      // Use intersection observer for normal zoom levels
      observer = new IntersectionObserver(handleIntersection, {
        threshold: [0, 0.05, 0.1, 0.15, 0.2],
        rootMargin: `-${scrollRevealInsetPx}px 0px 0px 0px`,
        root: null
      });

      observer.observe(scrollSentinelRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [handleIntersection, deviceConfig.enableAnimations]);

  // Reset on route change
  useEffect(() => {
    setIsScrolled(false);
    stableScrollStateRef.current = false;
    isIntersectingRef.current = true;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  const isActive = useCallback((path) => {
    return location.pathname === path;
  }, [location.pathname]);

  // Handle dropdown interactions
  const handleDropdownEnter = useCallback(() => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setDropdownOpen(true);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(false);
    }, 300);
  }, []);

  // Handle user menu interactions
  const handleUserMenuEnter = useCallback(() => {
    if (userMenuTimeoutRef.current) {
      clearTimeout(userMenuTimeoutRef.current);
    }
    setUserMenuOpen(true);
  }, []);

  const handleUserMenuLeave = useCallback(() => {
    userMenuTimeoutRef.current = setTimeout(() => {
      setUserMenuOpen(false);
    }, 300);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      setUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [logout, navigate]);

  // Navigate to Esanslar category
  const navigateToEsanslar = useCallback(() => {
    navigate('/products?category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46');
  }, [navigate]);

  // Navigate to Parfum Malzemeleri (all products)
  const navigateToParfumMalzemeleri = useCallback(() => {
    navigate('/products?category=0edcb00e-f5a5-4367-91cc-d90d82577e5e');
  }, [navigate]);

  // Navigate to specific category
  const navigateToCategory = useCallback((categoryId) => {
    navigate(`/products?category=${categoryId}`);
    setDropdownOpen(false);
  }, [navigate]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      if (userMenuTimeoutRef.current) {
        clearTimeout(userMenuTimeoutRef.current);
      }
    };
  }, []);


  return (
    <>
      {/* Intersection Observer Sentinel - Only render for normal zoom levels */}
      {!isHighZoom && (
        <div
          ref={scrollSentinelRef}
          className="fixed top-0 left-0 w-full h-1 pointer-events-none"
          style={{ zIndex: -1, opacity: 0 }}
        />
      )}
      
      <header
        ref={headerRef}
        className={`liquid-glass-header ${isScrolled ? 'scrolled' : ''}`}
        style={{overflow: 'visible'}}
      >
        {/* Header container - Progressive responsive design */}
        <div className="relative transition-all duration-300 mx-auto" style={{
          overflow: 'visible',
          maxWidth: deviceConfig.isUltraWide ? '1920px' : '100%',
          width: '100%',
          minHeight: deviceConfig.isMobile ? '48px' : '60px'
        }}>
          <div className={`relative transition-all duration-200 ${
            deviceConfig.isMobile ? 'px-3 py-1.5' :
            deviceConfig.isSmallLaptop ? 'px-4 py-2' :
            deviceConfig.isMediumLaptop ? 'px-6 py-2.5' :
            'px-8 py-2.5'
          }`} style={{overflow: 'visible'}}>
            <div className="flex items-center justify-between" style={{
              overflow: 'visible',
              minHeight: deviceConfig.isMobile ? '40px' : '48px',
              width: '100%'
            }}>
              {/* Logo - Responsive sizing */}
              <Link
                to="/"
                className="group relative overflow-hidden transition-all duration-300 flex items-center justify-center flex-shrink-0 hover:scale-105"
                onClick={closeMobileMenu}
                style={{ minWidth: 'fit-content' }}
              >
                {/* Hover effect for consistency */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none rounded-inherit bg-gradient-to-br from-white/20 to-transparent" />

                <img
                  key="felicita-logo"
                  src="/logo2.png"
                  alt={t('general.brand', 'Felicita Fragrances')}
                  className="relative object-contain transition-all duration-200"
                  style={{
                    height: deviceConfig.isMobile ? '28px' :
                           deviceConfig.isSmallLaptop ? '32px' :
                           deviceConfig.isMediumLaptop ? '36px' : '40px'
                  }}
                  loading="eager"
                  decoding="async"
                />
              </Link>

              {/* Desktop Navigation - ROBUST spacing and sizing */}
              <nav 
                ref={setNavElementRef}
                className={`hidden md:flex items-center flex-1 justify-center transition-all duration-300 ${
                  isHighZoom ? 'mx-2' : (deviceConfig.useCompactMode ? 'mx-1' : 'mx-2 lg:mx-4')
                }`} 
                style={{
                  // Stable gap for high zoom to prevent layout recalculations
                  gap: isHighZoom ? '12px' : (
                    deviceConfig.useMicroMode ? '6px' :
                    deviceConfig.useCompactMode ? '8px' :
                    deviceConfig.isMediumDesktop ? '12px' :
                    '16px'
                  ),
                  // Stable maxWidth for high zoom levels
                  maxWidth: isHighZoom ? '800px' : `${deviceConfig.availableNavWidth}px`,
                  minWidth: 'fit-content'
                }}
              >
                {/* Home Link */}
                <NavLink
                  key="/"
                  path="/"
                  isActive={isActive('/')}
                  label={t('nav.home')}
                  deviceConfig={deviceConfig}
                />
                
                {/* Esanslar Button */}
                <NavLink
                  path="/products?category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46"
                  isActive={location.pathname === '/products' && location.search.includes('category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46')}
                  label="Esanslar"
                  deviceConfig={deviceConfig}
                />

                {/* Parfüm Malzemeleri Button with Dropdown */}
                <div
                  className="relative"
                  onMouseEnter={handleDropdownEnter}
                  onMouseLeave={handleDropdownLeave}
                  ref={dropdownRef}
                >
                  <ParfumMalzemeleriButton
                    isActive={location.pathname === '/products' && !location.search.includes('category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46')}
                    onClick={navigateToParfumMalzemeleri}
                    dropdownOpen={dropdownOpen}
                    deviceConfig={deviceConfig}
                  />

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-gray-200 py-2 z-50">
                      {parfumMalzemeleriCategories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => navigateToCategory(category.id)}
                          className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rest of Navigation Items - ADAPTIVE hiding based on space */}
                {navItems.slice(1).map(({ path, key, fallback }, index) => {
                  // Define priority: higher number = more important
                  const priority = path === '/faq' ? 1 : 
                                 path === '/contact' ? 2 : 
                                 path === '/orders/search' ? 3 :
                                 path === '/blog' ? 4 :
                                 path === '/campaigns' ? 5 :
                                 path === '/configurator' ? 6 : 3;
                  
                  // Hide lower priority items when space is tight
                  const shouldShow = !deviceConfig.hideSecondaryItems || priority >= 3;
                  
                  if (!shouldShow) return null;
                  
                  return (
                    <NavLink
                      key={path}
                      path={path}
                      isActive={isActive(path)}
                      label={fallback ? t(key, fallback) : t(key)}
                      deviceConfig={deviceConfig}
                    />
                  );
                })}
              </nav>

              {/* Right Side Actions - ROBUST Responsive */}
              <div className="flex items-center flex-shrink-0" style={{
                overflow: 'visible',
                gap: deviceConfig.useMicroMode ? '6px' :
                     deviceConfig.useCompactMode ? '8px' :
                     deviceConfig.isMediumDesktop ? '10px' :
                     '12px',
                height: '100%',
                alignItems: 'center',
                minWidth: 'fit-content'
              }}>
                {/* User Icon - Desktop: Favorites, Mobile: User with conditional routing */}
                <Link
                  to={deviceConfig.isMobile ? (isAuthenticated ? "/account" : "/login") : "/account/favorites"}
                  className={`group relative overflow-hidden ${
                    deviceConfig.useMicroMode ? 'rounded-lg' :
                    deviceConfig.useCompactMode ? 'rounded-xl' : 'rounded-2xl'
                  } border shadow-sm flex items-center justify-center ${
                    deviceConfig.useMicroMode ? 'px-2 py-1.5' :
                    deviceConfig.useCompactMode ? 'px-2.5 py-2' :
                    deviceConfig.isMediumDesktop ? 'px-3 py-2' :
                    'px-4 py-2.5'
                  } ${
                    deviceConfig.enableAnimations && !deviceConfig.isMobile
                      ? 'transition-transform duration-200 hover:scale-105 bg-white/85 border-gray-200 hover:bg-white/90'
                      : 'bg-white/95 border-gray-300'
                  } min-w-[44px] min-h-[44px]`}
                  aria-label={deviceConfig.isMobile ? "Kullanıcı" : "Favoriler"}
                >
                  {!deviceConfig.isMobile && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-inherit bg-gradient-radial-small" />
                  )}

                  <div className="relative flex items-center justify-center">
                    {deviceConfig.isMobile ? (
                      // User icon for mobile
                      <svg
                        className="h-5 w-5 transition-transform duration-300 text-gray-700 group-hover:scale-110"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    ) : (
                      // Heart icon for desktop
                      <svg
                        className="h-5 w-5 transition-transform duration-300 text-gray-700 group-hover:scale-110"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                        />
                      </svg>
                    )}
                  </div>
                </Link>

                {/* Authentication Components */}
                {isAuthenticated ? (
                  // User Menu for authenticated users
                  <div
                    className="relative hidden md:block"
                    onMouseEnter={handleUserMenuEnter}
                    onMouseLeave={handleUserMenuLeave}
                    ref={userMenuRef}
                  >
                    <Link
                      to="/account"
                      className={`group relative overflow-hidden ${
                        deviceConfig.useMicroMode ? 'rounded-lg' :
                        deviceConfig.useCompactMode ? 'rounded-xl' : 'rounded-2xl'
                      } border shadow-sm flex items-center justify-center ${
                        deviceConfig.useMicroMode ? 'px-2 py-1.5' :
                        deviceConfig.useCompactMode ? 'px-2.5 py-2' :
                        deviceConfig.isMediumDesktop ? 'px-3 py-2' :
                        'px-4 py-2.5'
                      } ${
                        deviceConfig.enableAnimations && !deviceConfig.isMobile
                          ? 'transition-transform duration-200 hover:scale-105 bg-white/85 border-gray-200 hover:bg-white/90'
                          : 'bg-white/95 border-gray-300'
                      } min-w-[44px] min-h-[44px]`}
                      aria-label="Kullanıcı Hesabı"
                    >
                      {!deviceConfig.isMobile && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-inherit bg-gradient-radial-small" />
                      )}

                      <div className="relative flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 transition-transform duration-300 text-gray-700 group-hover:scale-110"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    </Link>

                    {/* User Dropdown Menu */}
                    {userMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          {/* Menu Items */}
                          <Link
                            to="/account/orders"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Siparişlerim
                          </Link>
                          <Link
                            to="/account/favorites"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            Favorilerim
                          </Link>
                          <Link
                            to="/account/lists"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Listelerim
                          </Link>
                          <Link
                            to="/account"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Hesabım
                          </Link>
                          <Link
                            to="/account/addresses"
                            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Adreslerim
                          </Link>

                          <div className="border-t border-gray-200 my-1"></div>

                          <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" />
                            </svg>
                            Çıkış Yap
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // User icon with dropdown for non-authenticated users
                  <div
                    className="relative hidden md:block"
                    onMouseEnter={handleUserMenuEnter}
                    onMouseLeave={handleUserMenuLeave}
                    ref={userMenuRef}
                  >
                    <button
                      className={`group relative overflow-hidden ${
                        deviceConfig.useMicroMode ? 'rounded-lg' :
                        deviceConfig.useCompactMode ? 'rounded-xl' : 'rounded-2xl'
                      } border shadow-sm flex items-center justify-center ${
                        deviceConfig.useMicroMode ? 'px-2 py-1.5' :
                        deviceConfig.useCompactMode ? 'px-2.5 py-2' :
                        deviceConfig.isMediumDesktop ? 'px-3 py-2' :
                        'px-4 py-2.5'
                      } ${
                        deviceConfig.enableAnimations && !deviceConfig.isMobile
                          ? 'transition-transform duration-200 hover:scale-105 bg-white/85 border-gray-200 hover:bg-white/90'
                          : 'bg-white/95 border-gray-300'
                      } min-w-[44px] min-h-[44px]`}
                      aria-label="Giriş/Kayıt Menüsü"
                    >
                      {!deviceConfig.isMobile && (
                        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-inherit bg-gradient-radial-small" />
                      )}

                      <div className="relative flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 transition-transform duration-300 text-gray-700 group-hover:scale-110"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.8}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                    </button>

                    {/* Auth Dropdown Menu */}
                    {userMenuOpen && (
                      <div className="absolute right-0 top-full mt-2 w-48 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1">
                          <Link
                            to="/login"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14" />
                            </svg>
                            Giriş Yap
                          </Link>
                          <Link
                            to="/register"
                            className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                          >
                            <svg className="mr-3 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                            </svg>
                            Kayıt Ol
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ROBUST Cart Button - maintains usability */}
                <Link
                  to="/cart"
                  className={`group relative overflow-hidden ${
                    deviceConfig.useMicroMode ? 'rounded-lg' :
                    deviceConfig.useCompactMode ? 'rounded-xl' : 'rounded-2xl'
                  } border shadow-sm flex items-center justify-center ${
                    deviceConfig.isMobile ? 'px-3 py-2' :
                    deviceConfig.useMicroMode ? 'px-2 py-1.5' :
                    deviceConfig.useCompactMode ? 'px-2.5 py-2' :
                    deviceConfig.isMediumDesktop ? 'px-3 py-2' :
                    'px-4 py-2.5'
                  } ${
                    deviceConfig.enableAnimations && !deviceConfig.isMobile
                      ? 'transition-transform duration-200 hover:scale-105 bg-white/85 border-gray-200 hover:bg-white/90'
                      : 'bg-white/95 border-gray-300'
                  } min-w-[44px] min-h-[44px]`}
                  aria-label={t('nav.cart')}
                >
                  {!deviceConfig.isMobile && (
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-inherit bg-gradient-radial-small" />
                  )}

                  <div className="relative flex items-center justify-center space-x-2 h-full">
                    <div className="relative flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 transition-transform duration-300 text-gray-700 group-hover:scale-110"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                        />
                      </svg>

                      {cartItemCount > 0 && (
                        <span className="absolute -top-2 -right-2 h-5 w-5 min-w-[1.25rem] px-0.5 text-white text-[10px] font-bold rounded-full flex items-center justify-center bg-red-600 shadow-lg sm:text-xs">
                          {cartItemCount > 99 ? '99+' : cartItemCount}
                        </span>
                      )}
                    </div>

                    {deviceConfig.showCartText && (
                      <span className="text-sm font-semibold text-gray-700 leading-none flex items-center">
                        {t('nav.cart', 'Cart')}
                      </span>
                    )}
                  </div>
                </Link>

                {/* Ultra-Compact Mobile Menu Toggle */}
                <button
                  className={`md:hidden ${
                    deviceConfig.useCompactMode ? 'rounded-lg p-1' : 'rounded-xl p-1.5'
                  } border shadow-sm bg-white/95 border-gray-300 flex items-center justify-center`}
                  onClick={toggleMobileMenu}
                  aria-label={t('nav.toggleMobileMenu')}
                  style={{ 
                    minWidth: '40px', 
                    minHeight: '40px',
                    flexShrink: 0 
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 transition-transform duration-300 text-gray-700 ${mobileMenuOpen ? 'rotate-90' : 'rotate-0'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    {mobileMenuOpen ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 6h16M4 12h16M4 18h16"
                      />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <MobileMenu
                navItems={navItems}
                isActive={isActive}
                closeMobileMenu={closeMobileMenu}
                t={t}
                navigateToEsanslar={navigateToEsanslar}
                navigateToParfumMalzemeleri={navigateToParfumMalzemeleri}
                navigateToCategory={navigateToCategory}
                parfumMalzemeleriCategories={parfumMalzemeleriCategories}
                location={location}
                isAuthenticated={isAuthenticated}
                user={user}
                handleLogout={handleLogout}
              />
            )}
          </div>
        </div>
      </header>
    </>
  );
};

// ULTRA-RESPONSIVE Navigation Link Component
const NavLink = React.memo(({ path, isActive, label, deviceConfig }) => {
  const linkStyles = useMemo(() => {
    const baseStyles = {
      transform: 'translateZ(0)',
      contain: 'layout style paint'
    };
    
    if (deviceConfig.isMobile || deviceConfig.isLowEnd) {
      return {
        ...baseStyles,
        background: isActive ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        border: `1px solid ${isActive ? 'rgba(17,24,39,0.4)' : 'rgba(200,200,200,0.4)'}`,
        color: isActive ? '#ffffff' : '#374151',
        boxShadow: 'none'
      };
    }
    
    return {
      ...baseStyles,
      background: isActive ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      border: `1px solid ${isActive ? 'rgba(17,24,39,0.4)' : 'rgba(200,200,200,0.5)'}`,
      color: isActive ? '#ffffff' : '#374151',
      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
    };
  }, [isActive, deviceConfig.isMobile, deviceConfig.isLowEnd]);

  // ROBUST: Dynamic sizing with minimum usability thresholds
  const getResponsiveClass = () => {
    if (deviceConfig.isMobile || deviceConfig.isLowEnd) {
      return "block rounded-xl px-3 py-2.5 text-sm font-medium whitespace-nowrap";
    }
    
    // Micro mode - smallest but still usable (emergency mode)
    if (deviceConfig.useMicroMode) {
      return "group relative overflow-hidden rounded-lg px-2 py-1.5 text-xs font-medium transition-all duration-200 whitespace-nowrap min-w-[60px] text-center";
    }
    
    // Compact mode - smaller but readable
    if (deviceConfig.useCompactMode) {
      return "group relative overflow-hidden rounded-xl px-2.5 py-2 text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-[70px] text-center";
    }
    
    // Medium desktops - balanced size
    if (deviceConfig.isMediumDesktop) {
      return "group relative overflow-hidden rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 whitespace-nowrap";
    }
    
    // Large screens - comfortable size
    return "group relative overflow-hidden rounded-2xl px-4 py-2.5 text-sm font-medium transition-all duration-200 whitespace-nowrap";
  };

  return (
    <Link
      to={path}
      className={getResponsiveClass()}
      style={linkStyles}
    >
      {!deviceConfig.isMobile && !deviceConfig.isLowEnd && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-inherit pointer-events-none bg-gradient-to-br from-white/25 to-transparent" />
      )}
      <span className="relative z-10">{label}</span>
    </Link>
  );
});

NavLink.displayName = 'NavLink';

// Ultra-Responsive Parfüm Malzemeleri Button Component
const ParfumMalzemeleriButton = React.memo(({ isActive, onClick, dropdownOpen, deviceConfig }) => {
  const buttonStyles = useMemo(() => {
    const baseStyles = {
      transform: 'translateZ(0)',
      contain: 'layout style paint'
    };
    
    if (deviceConfig.isMobile || deviceConfig.isLowEnd) {
      return {
        ...baseStyles,
        background: isActive ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'none',
        WebkitBackdropFilter: 'none',
        border: `1px solid ${isActive ? 'rgba(17,24,39,0.4)' : 'rgba(200,200,200,0.4)'}`,
        color: isActive ? '#ffffff' : '#374151',
        boxShadow: 'none'
      };
    }
    
    return {
      ...baseStyles,
      background: isActive ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      border: `1px solid ${isActive ? 'rgba(17,24,39,0.4)' : 'rgba(200,200,200,0.5)'}`,
      color: isActive ? '#ffffff' : '#374151',
      boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'
    };
  }, [isActive, deviceConfig.isMobile, deviceConfig.isLowEnd]);

  // ROBUST: Dynamic sizing with minimum usability for dropdown button
  const getResponsiveClass = () => {
    if (deviceConfig.isMobile || deviceConfig.isLowEnd) {
      return "flex items-center gap-1 rounded-xl px-3 py-2.5 text-sm font-medium whitespace-nowrap";
    }
    
    // Micro mode - maintain readability for important dropdown
    if (deviceConfig.useMicroMode) {
      return "group relative overflow-hidden rounded-lg px-2 py-1.5 text-xs font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-0.5 min-w-[80px] justify-center";
    }
    
    // Compact mode - smaller but still functional
    if (deviceConfig.useCompactMode) {
      return "group relative overflow-hidden rounded-xl px-2.5 py-2 text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1 min-w-[90px] justify-center";
    }
    
    // Medium desktops - balanced
    if (deviceConfig.isMediumDesktop) {
      return "group relative overflow-hidden rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1";
    }
    
    // Large screens - comfortable
    return "group relative overflow-hidden rounded-2xl px-4 py-2.5 text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1";
  };

  // SMART: Progressive text shortening based on actual space constraints
  const getButtonText = () => {
    if (deviceConfig.useMicroMode) {
      return "Parfüm Şişeleri"; // Very short for emergency space
    }
    if (deviceConfig.useCompactMode) {
      return "Parfüm Şişeleri"; // Shortened version
    }
    return "Parfüm Şişeleri"; // Full text
  };

  return (
    <button
      onClick={onClick}
      className={getResponsiveClass()}
      style={buttonStyles}
    >
      {!deviceConfig.isMobile && !deviceConfig.isLowEnd && (
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-inherit pointer-events-none bg-gradient-to-br from-white/25 to-transparent" />
      )}
      <span className="relative z-10">{getButtonText()}</span>
      <svg
        className={`relative z-10 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''} ${
          deviceConfig.useCompactMode ? 'w-3 h-3' : 'w-4 h-4'
        }`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );
});

ParfumMalzemeleriButton.displayName = 'ParfumMalzemeleriButton';

// PERFORMANCE OPTIMIZED: Ultra-fast Mobile Menu Component
const MobileMenu = React.memo(({ navItems, isActive, closeMobileMenu, t, navigateToEsanslar, navigateToParfumMalzemeleri, navigateToCategory, parfumMalzemeleriCategories, location, isAuthenticated, user, handleLogout }) => {
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  return (
    <div className="md:hidden mt-6 relative">
      <div className="rounded-2xl p-4 bg-white border border-gray-300 shadow-lg">
        <nav className="flex flex-col space-y-1">
          {/* Home Link */}
          <Link
            to="/"
            className="block rounded-lg px-3 py-2 text-sm font-medium"
            style={{
              background: isActive('/') ? 'rgba(17, 24, 39, 0.95)' : 'transparent',
              color: isActive('/') ? '#ffffff' : '#374151',
              border: `1px solid ${isActive('/') ? 'rgba(255,255,255,0.2)' : 'transparent'}`
            }}
            onClick={closeMobileMenu}
          >
            {t('nav.home')}
          </Link>
          
          {/* Esanslar Button */}
          <Link
            to="/products?category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46"
            className="block rounded-lg px-3 py-2 text-sm font-medium"
            style={{
              background: (location.pathname === '/products' && location.search.includes('category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46')) ? 'rgba(17, 24, 39, 0.95)' : 'transparent',
              color: (location.pathname === '/products' && location.search.includes('category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46')) ? '#ffffff' : '#374151',
              border: `1px solid ${(location.pathname === '/products' && location.search.includes('category=adc17f7c-68a3-40f6-8a2c-2559a91c6b46')) ? 'rgba(255,255,255,0.2)' : 'transparent'}`
            }}
            onClick={closeMobileMenu}
          >
            Esanslar
          </Link>
          
          {/* Parfüm Malzemeleri with Dropdown */}
          <div>
            <button
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
              className="flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium w-full"
              style={{
                background: mobileDropdownOpen ? 'rgba(243, 244, 246, 0.5)' : 'transparent',
                color: '#374151',
                border: '1px solid transparent'
              }}
            >
              <span>Parfüm Malzemeleri</span>
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {mobileDropdownOpen && (
              <div className="ml-3 mt-1 space-y-1">
                <button
                  onClick={() => {
                    navigateToParfumMalzemeleri();
                    closeMobileMenu();
                  }}
                  className="block rounded-lg px-3 py-1.5 text-xs font-medium w-full text-left text-gray-600 hover:bg-gray-50"
                >
                  Tüm Ürünler
                </button>
                {parfumMalzemeleriCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      navigateToCategory(category.id);
                      closeMobileMenu();
                    }}
                    className="block rounded-lg px-3 py-1.5 text-xs font-medium w-full text-left text-gray-600 hover:bg-gray-50"
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rest of Navigation Items */}
          {navItems.slice(1).map(({ path, key, fallback }) => (
            <Link
              key={path}
              to={path}
              className="block rounded-lg px-3 py-2 text-sm font-medium"
              style={{
                background: isActive(path) ? 'rgba(17, 24, 39, 0.95)' : 'transparent',
                color: isActive(path) ? '#ffffff' : '#374151',
                border: `1px solid ${isActive(path) ? 'rgba(255,255,255,0.2)' : 'transparent'}`
              }}
              onClick={closeMobileMenu}
            >
              {fallback ? t(key, fallback) : t(key)}
            </Link>
          ))}
        </nav>

        {/* Mobile Authentication Section */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          {isAuthenticated ? (
            <div>
              {/* User Info */}
              <div className="px-2 mb-4">
                <div className="flex items-center space-x-3 p-3 rounded-xl bg-gray-50 border">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {user?.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Account Navigation */}
              <div className="px-2 space-y-1">
                <Link
                  to="/account"
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={closeMobileMenu}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Hesabım</span>
                </Link>
                <Link
                  to="/account/orders"
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={closeMobileMenu}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Siparişlerim</span>
                </Link>
                <Link
                  to="/account/favorites"
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={closeMobileMenu}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span>Favorilerim</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 w-full"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" />
                  </svg>
                  <span>Çıkış Yap</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="px-2 space-y-3">
              <Link
                to="/login"
                className="flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={closeMobileMenu}
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                className="flex items-center justify-center px-4 py-3 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors"
                onClick={closeMobileMenu}
              >
                Kayıt Ol
              </Link>
            </div>
          )}
        </div>

      </div>
    </div>
  );
});

MobileMenu.displayName = 'MobileMenu';

export default Header;